import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, ChevronRight, Loader2, Sparkles, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/app/api";
import { CommissionRuleEditor, DocumentRequirementsEditor, SopSectionsEditor } from "./PartnerRequirements";

const STEPS = ["Partner", "Providers", "Programmes", "Opportunities", "Submission & commission", "Review & publish"];
const PARTNER_TYPES = [["recruitment_agent", "Recruitment agent"], ["affiliate_network", "Affiliate network"], ["education_provider_group", "Education provider group"], ["service_partner", "Service partner"], ["other", "Other"]];
const SUBMISSION_TYPES = [["email", "Email"], ["portal", "Partner portal"], ["api", "API"], ["crm", "CRM"], ["manual", "Manual / admin"]];
const COMMISSION_TYPES = [["percentage", "Percentage"], ["fixed_amount", "Fixed amount"], ["tiered_percentage", "Tiered percentage"], ["tiered_fixed", "Tiered fixed"]];
const EVENTS = [["application_submitted", "Application submitted"], ["application_accepted", "Application accepted"], ["learner_enrolled", "Learner enrolled"], ["learner_started", "Learner started"], ["commission_received", "Commission received"]];
const MAPPING_TARGETS = [
  "learner.identity.first_name", "learner.identity.last_name", "learner.identity.date_of_birth",
  "learner.contact.email", "learner.contact.mobile", "learner.contact.address",
  "learner.location.country", "learner.location.city",
  "learner.education.highest_level", "learner.education.institution", "learner.education.qualification_name",
  "learner.education.country", "learner.education.completion",
  "learner.english.status", "learner.english.test", "learner.employment.experience",
  "learner.documents.passport", "learner.documents.transcript", "learner.documents.certificate",
  "learner.documents.cv", "learner.documents.sop", "learner.documents.references",
  "learner.documents.visa_refusals", "learner.international.visa_status",
  "application.provider", "application.programme", "application.opportunity", "application.intake",
  "application.funding_intention", "application.tuition_budget", "application.deposit_budget",
  "application.notes", "other",
];

const input = "w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-cobalt focus:ring-2 focus:ring-cobalt/15";
const label = "mb-1 block text-xs font-bold text-slatey";
const EMPTY = {
  name: "", partner_type: "recruitment_agent", markets: "", market_notes: "", website: "",
  contact_name: "", contact_email: "", internal_notes: "", active: false,
  provider_ids: [], provider_links: [],
  submission_method: { type: "email", recipient: "", subject_format: "", body_fields: [], required_attachments: [], conditional_attachments: [], instructions: "" },
  commission_rules: [], document_requirements: [], sop_sections: [],
};

export default function PartnerConfig() {
  const { partnerId } = useParams();
  const isNew = !partnerId || partnerId === "new";
  const [id, setId] = useState(isNew ? null : partnerId);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null);
  const [providers, setProviders] = useState([]);
  const [allOpportunities, setAllOpportunities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newProviderName, setNewProviderName] = useState("");
  const [pickProvider, setPickProvider] = useState("");
  const [routeType, setRouteType] = useState("direct");
  const [bulkText, setBulkText] = useState("");
  const [bulkRoute, setBulkRoute] = useState("direct");
  const [programme, setProgramme] = useState({ provider_id: "", title: "", subject_area: "", qualification_level: "" });
  const [pickOpportunity, setPickOpportunity] = useState("");
  const [pickProgramme, setPickProgramme] = useState("");
  const [formUpload, setFormUpload] = useState({ name: "", content_text: "" });
  const [activeForm, setActiveForm] = useState(null);
  const [analysing, setAnalysing] = useState(false);

  const load = async (pid = id) => {
    if (!pid) return;
    const response = await api.get(`/admin/partners/${pid}`);
    const { providers: partnerProviders, programmes, opportunities, forms, ...partner } = response.data;
    setDetail({ providers: partnerProviders, programmes, opportunities, forms });
    setForm({
      ...EMPTY, ...partner,
      markets: (partner.markets || []).join(", "),
      submission_method: { ...EMPTY.submission_method, ...(partner.submission_method || {}) },
    });
  };

  useEffect(() => { if (id) load(id); }, [id]);
  useEffect(() => {
    api.get("/admin/partners").catch(() => {});
    api.get("/opportunities").then((response) => setAllOpportunities(response.data.opportunities || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (id && step === 1) api.get(`/admin/partners/${id}/available-providers`).then((response) => setProviders(response.data.providers)).catch(() => {});
  }, [id, step]);

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const setSub = (key) => (event) => setForm((current) => ({ ...current, submission_method: { ...current.submission_method, [key]: event.target.value } }));

  const payload = () => ({
    ...form,
    markets: form.markets.split(",").map((m) => m.trim()).filter(Boolean),
    submission_method: {
      ...form.submission_method,
      body_fields: typeof form.submission_method.body_fields === "string" ? form.submission_method.body_fields.split(",").map((s) => s.trim()).filter(Boolean) : form.submission_method.body_fields,
      required_attachments: typeof form.submission_method.required_attachments === "string" ? form.submission_method.required_attachments.split(",").map((s) => s.trim()).filter(Boolean) : form.submission_method.required_attachments,
    },
  });

  const save = async (advance = false) => {
    setSaving(true);
    try {
      if (!id) {
        const response = await api.post("/admin/partners", payload());
        setId(response.data.partner.id);
        toast.success("Partner draft created.");
      } else {
        await api.patch(`/admin/partners/${id}`, payload());
        toast.success("Saved.");
      }
      if (advance) setStep((current) => Math.min(current + 1, STEPS.length - 1));
      if (id) await load();
    } catch (requestError) {
      toast.error(formatApiErrorDetail(requestError.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const transition = async (to) => {
    try {
      await api.post(`/admin/partners/${id}/transition`, { to });
      toast.success(`Configuration ${to}.`);
      await load();
    } catch (requestError) {
      toast.error(formatApiErrorDetail(requestError.response?.data?.detail));
    }
  };

  const addProvider = async () => {
    try {
      await api.post(`/admin/partners/${id}/providers`, pickProvider ? { provider_id: pickProvider, route_type: routeType } : { new_provider_name: newProviderName, route_type: routeType });
      setPickProvider(""); setNewProviderName("");
      await load();
    } catch (requestError) { toast.error(formatApiErrorDetail(requestError.response?.data?.detail)); }
  };

  const bulkImport = async () => {
    try {
      const names = bulkText.split("\n").map((line) => line.trim()).filter(Boolean);
      const response = await api.post(`/admin/partners/${id}/providers/bulk`, {
        providers: names.map((name) => ({ name, route_type: bulkRoute })),
      });
      toast.success(`Imported: ${response.data.created} new providers, ${response.data.linked} linked.`);
      setBulkText("");
      await load();
    } catch (requestError) { toast.error(formatApiErrorDetail(requestError.response?.data?.detail)); }
  };

  const addProgramme = async () => {
    try {
      await api.post(`/admin/partners/${id}/programmes`, programme);
      setProgramme({ provider_id: "", title: "", subject_area: "", qualification_level: "" });
      await load();
    } catch (requestError) { toast.error(formatApiErrorDetail(requestError.response?.data?.detail)); }
  };

  const addOpportunity = async () => {
    try {
      await api.post(`/admin/partners/${id}/opportunities`, { opportunity_id: pickOpportunity, programme_id: pickProgramme || undefined });
      setPickOpportunity(""); setPickProgramme("");
      await load();
    } catch (requestError) { toast.error(formatApiErrorDetail(requestError.response?.data?.detail)); }
  };

  const uploadForm = async () => {
    try {
      const response = await api.post(`/admin/partners/${id}/forms`, formUpload);
      setFormUpload({ name: "", content_text: "" });
      toast.success("Form uploaded. Now analyse it with AI.");
      setActiveForm(response.data.form.id);
      await load();
    } catch (requestError) { toast.error(formatApiErrorDetail(requestError.response?.data?.detail)); }
  };

  const analyseForm = async (formId) => {
    setAnalysing(true);
    try {
      const response = await api.post(`/admin/partners/${id}/forms/${formId}/analyse`);
      toast.success(response.data.ai_assisted ? "AI proposed field mappings — review them below." : "AI unavailable — map the fields manually.");
      setActiveForm(formId);
      await load();
    } catch (requestError) { toast.error(formatApiErrorDetail(requestError.response?.data?.detail)); }
    finally { setAnalysing(false); }
  };

  const setFieldMapping = async (formId, fields) => {
    try {
      await api.patch(`/admin/partners/${id}/forms/${formId}`, { fields });
      toast.success("Mappings saved.");
      await load();
    } catch (requestError) { toast.error(formatApiErrorDetail(requestError.response?.data?.detail)); }
  };

  const publishForm = async (formId) => {
    try {
      await api.post(`/admin/partners/${id}/forms/${formId}/publish`);
      toast.success("Form configuration published.");
      await load();
    } catch (requestError) { toast.error(formatApiErrorDetail(requestError.response?.data?.detail)); }
  };

  const config = form.config || {};
  const configStatus = form.config?.status || (id ? "draft" : null);

  return (
    <div data-testid="admin-partner-config" className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-ink">{isNew && !id ? "Add a partner" : form.name || "Partner configuration"}</h1>
        <p className="mt-1 text-sm text-slatey">Guided setup — six simple steps. Nothing goes live until you publish it.</p>
      </div>

      <ol className="flex gap-2 overflow-x-auto rounded-3xl border border-slate-200 bg-white p-3">
        {STEPS.map((labelText, index) => (
          <li key={labelText}>
            <button type="button" onClick={() => (id || index === 0) && setStep(index)} disabled={!id && index > 0} data-testid={`partner-step-${index}`}
              className={`flex min-w-max items-center gap-2 rounded-full px-3 py-2 text-xs font-bold ${index === step ? "bg-cobalt text-white" : id ? "bg-surface text-ink hover:bg-slate-100" : "bg-surface text-slatey opacity-50"}`}>
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/20">{index + 1}</span>{labelText}
            </button>
          </li>
        ))}
      </ol>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8">
        {step === 0 && (
          <div className="grid gap-4 sm:grid-cols-2" data-testid="partner-step-details">
            <div><span className={label}>Partner name</span><input value={form.name} onChange={set("name")} data-testid="partner-name" className={input} placeholder="For example: BWBS Education Consultancy" /></div>
            <div><span className={label}>Partner type</span><select value={form.partner_type} onChange={set("partner_type")} data-testid="partner-type" className={input}>{PARTNER_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            <div><span className={label}>Countries/markets (comma separated)</span><input value={form.markets} onChange={set("markets")} data-testid="partner-markets" className={input} placeholder="International, South Asia…" /></div>
            <div><span className={label}>Website</span><input value={form.website || ""} onChange={set("website")} data-testid="partner-website" className={input} placeholder="https://…" /></div>
            <div><span className={label}>Contact name</span><input value={form.contact_name || ""} onChange={set("contact_name")} data-testid="partner-contact-name" className={input} /></div>
            <div><span className={label}>Contact email</span><input value={form.contact_email || ""} onChange={set("contact_email")} data-testid="partner-contact-email" className={input} /></div>
            <div className="sm:col-span-2"><span className={label}>Internal notes</span><textarea value={form.internal_notes || ""} onChange={set("internal_notes")} data-testid="partner-notes" rows={3} className={input} /></div>
            <label className="flex items-center gap-3 text-sm font-semibold text-ink"><input type="checkbox" checked={form.active} onChange={(e) => setForm((c) => ({ ...c, active: e.target.checked }))} data-testid="partner-active" className="h-4 w-4 accent-cobalt" /> Partner is active</label>
            <div className="sm:col-span-2">
              <button onClick={() => save(true)} disabled={saving || form.name.trim().length < 2} data-testid="partner-save-continue" className="inline-flex items-center gap-2 rounded-full bg-cobalt px-6 py-3 text-sm font-bold text-white disabled:opacity-50">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} {id ? "Save & continue" : "Create draft & continue"} <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {step === 1 && detail && (
          <div data-testid="partner-step-providers">
            <h2 className="font-display text-xl font-bold text-ink">Providers / universities this partner works with</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_160px_auto]">
              <select value={pickProvider} onChange={(e) => setPickProvider(e.target.value)} data-testid="partner-provider-pick" className={input}>
                <option value="">Select an existing provider…</option>
                {providers.filter((p) => !(form.provider_ids || []).includes(p.id)).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <select value={routeType} onChange={(e) => setRouteType(e.target.value)} data-testid="partner-provider-route" className={input}>
                <option value="direct">Direct route</option><option value="pathway">Pathway route</option>
              </select>
              <button onClick={addProvider} disabled={!pickProvider && !newProviderName.trim()} data-testid="partner-provider-add" className="rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white disabled:opacity-50">Add</button>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-[1fr]">
              <input value={newProviderName} onChange={(e) => setNewProviderName(e.target.value)} data-testid="partner-provider-new" className={input} placeholder="…or type a new provider/university name" />
            </div>
            <div className="mt-4 rounded-2xl border border-dashed border-slate-300 p-4">
              <p className="text-xs font-bold uppercase text-slatey">Bulk import a university list</p>
              <div className="mt-2 grid gap-3 sm:grid-cols-[1fr_160px_auto]">
                <textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} data-testid="partner-bulk-import" rows={3} className={input} placeholder="One provider/university per line…" />
                <select value={bulkRoute} onChange={(e) => setBulkRoute(e.target.value)} data-testid="partner-bulk-route" className={input}>
                  <option value="direct">Direct route</option><option value="pathway">Pathway route</option>
                </select>
                <button onClick={bulkImport} disabled={!bulkText.trim()} data-testid="partner-bulk-submit" className="rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white disabled:opacity-50">Import</button>
              </div>
            </div>
            <ul className="mt-5 max-h-96 space-y-2 overflow-y-auto">
              {(detail.providers || []).map((provider) => (
                <li key={provider.id} className="flex items-center justify-between rounded-2xl bg-surface px-4 py-3 text-sm" data-testid={`partner-provider-${provider.id}`}>
                  <span className="font-semibold text-ink">{provider.name} <span className="ml-2 text-xs text-slatey">{provider.provider_type}</span>
                    {(provider.route_types || []).map((route) => <span key={route} className="ml-1 rounded-full bg-cobalt/10 px-2 py-0.5 text-[10px] font-bold uppercase text-cobalt">{route}</span>)}
                  </span>
                  <button onClick={async () => { await api.delete(`/admin/partners/${id}/providers/${provider.id}`); await load(); }} data-testid={`partner-provider-remove-${provider.id}`} className="text-xs font-bold text-rose-600">Remove</button>
                </li>
              ))}
            </ul>
            <StepNav onBack={() => setStep(0)} onNext={() => { save(false); setStep(2); }} />
          </div>
        )}

        {step === 2 && detail && (
          <div data-testid="partner-step-programmes">
            <h2 className="font-display text-xl font-bold text-ink">Programmes</h2>
            <p className="mt-1 text-sm text-slatey">A programme is the academic course family (for example BSc Computer Science) that opportunities belong to.</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <select value={programme.provider_id} onChange={(e) => setProgramme((c) => ({ ...c, provider_id: e.target.value }))} data-testid="programme-provider" className={input}>
                <option value="">Provider…</option>
                {(detail.providers || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <input value={programme.title} onChange={(e) => setProgramme((c) => ({ ...c, title: e.target.value }))} data-testid="programme-title" className={input} placeholder="Programme title" />
              <input value={programme.subject_area} onChange={(e) => setProgramme((c) => ({ ...c, subject_area: e.target.value }))} data-testid="programme-subject" className={input} placeholder="Subject area" />
              <input value={programme.qualification_level} onChange={(e) => setProgramme((c) => ({ ...c, qualification_level: e.target.value }))} data-testid="programme-level" className={input} placeholder="Level (e.g. undergraduate)" />
            </div>
            <button onClick={addProgramme} disabled={!programme.provider_id || programme.title.trim().length < 2} data-testid="programme-add" className="mt-3 rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white disabled:opacity-50">Add programme</button>
            <ul className="mt-5 space-y-2">
              {(detail.programmes || []).map((prog) => (
                <li key={prog.id} className="flex items-center justify-between rounded-2xl bg-surface px-4 py-3 text-sm" data-testid={`programme-${prog.id}`}>
                  <span className="font-semibold text-ink">{prog.title} <span className="ml-2 text-xs text-slatey">{prog.subject_area} · {prog.qualification_level}</span></span>
                  <button onClick={async () => { await api.delete(`/admin/partners/${id}/programmes/${prog.id}`); await load(); }} data-testid={`programme-remove-${prog.id}`} className="text-xs font-bold text-rose-600">Remove</button>
                </li>
              ))}
            </ul>
            <StepNav onBack={() => setStep(1)} onNext={() => setStep(3)} />
          </div>
        )}

        {step === 3 && detail && (
          <div data-testid="partner-step-opportunities">
            <h2 className="font-display text-xl font-bold text-ink">Opportunities</h2>
            <p className="mt-1 text-sm text-slatey">Associate existing OpenDegree opportunities with this partner (and optionally a programme). Intakes and requirements come from the opportunity itself.</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <select value={pickOpportunity} onChange={(e) => setPickOpportunity(e.target.value)} data-testid="opportunity-pick" className={input}>
                <option value="">Select opportunity…</option>
                {allOpportunities.filter((o) => !(detail.opportunities || []).some((existing) => existing.id === o.id)).map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}
              </select>
              <select value={pickProgramme} onChange={(e) => setPickProgramme(e.target.value)} data-testid="opportunity-programme" className={input}>
                <option value="">Programme (optional)…</option>
                {(detail.programmes || []).map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
              <button onClick={addOpportunity} disabled={!pickOpportunity} data-testid="opportunity-add" className="rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white disabled:opacity-50">Associate</button>
            </div>
            <ul className="mt-5 space-y-2">
              {(detail.opportunities || []).map((opp) => (
                <li key={opp.id} className="flex items-center justify-between rounded-2xl bg-surface px-4 py-3 text-sm" data-testid={`partner-opportunity-${opp.id}`}>
                  <span className="font-semibold text-ink">{opp.title} <span className="ml-2 text-xs text-slatey">{opp.qualification_level}</span></span>
                  <button onClick={async () => { await api.delete(`/admin/partners/${id}/opportunities/${opp.id}`); await load(); }} data-testid={`partner-opportunity-remove-${opp.id}`} className="text-xs font-bold text-rose-600">Remove</button>
                </li>
              ))}
            </ul>
            <StepNav onBack={() => setStep(2)} onNext={() => setStep(4)} />
          </div>
        )}

        {step === 4 && detail && (
          <div data-testid="partner-step-submission">
            <h2 className="font-display text-xl font-bold text-ink">Submission method</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div><span className={label}>Method</span><select value={form.submission_method.type} onChange={setSub("type")} data-testid="submission-type" className={input}>{SUBMISSION_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
              <div><span className={label}>Recipient (for email submissions)</span><input value={form.submission_method.recipient || ""} onChange={setSub("recipient")} data-testid="submission-recipient" className={input} placeholder="applications@partner.com" /></div>
              <div className="sm:col-span-2"><span className={label}>Subject format</span><input value={form.submission_method.subject_format || ""} onChange={setSub("subject_format")} data-testid="submission-subject" className={input} placeholder="OpenDegree Application — {applicant_name} — {programme} — {intake}" /></div>
              <div><span className={label}>Required email body fields (comma separated)</span><input value={Array.isArray(form.submission_method.body_fields) ? form.submission_method.body_fields.join(", ") : form.submission_method.body_fields} onChange={setSub("body_fields")} data-testid="submission-body-fields" className={input} /></div>
              <div><span className={label}>Required attachments (comma separated)</span><input value={Array.isArray(form.submission_method.required_attachments) ? form.submission_method.required_attachments.join(", ") : form.submission_method.required_attachments} onChange={setSub("required_attachments")} data-testid="submission-attachments" className={input} /></div>
              <div className="sm:col-span-2"><span className={label}>Submission instructions</span><textarea value={form.submission_method.instructions || ""} onChange={setSub("instructions")} data-testid="submission-instructions" rows={3} className={input} /></div>
            </div>

            <h2 className="mt-8 font-display text-xl font-bold text-ink">Document requirements</h2>
            <p className="mt-1 text-sm text-slatey">What this partner requires with an application. Conditional documents must not block learners when the condition does not apply.</p>
            <DocumentRequirementsEditor items={form.document_requirements || []} onChange={(items) => setForm((c) => ({ ...c, document_requirements: items }))} />

            <h2 className="mt-8 font-display text-xl font-bold text-ink">SOP / application questions</h2>
            <p className="mt-1 text-sm text-slatey">Partner-specific statement-of-purpose sections. Only shown to learners applying through this partner.</p>
            <SopSectionsEditor items={form.sop_sections || []} onChange={(items) => setForm((c) => ({ ...c, sop_sections: items }))} />

            <h2 className="mt-8 font-display text-xl font-bold text-ink">Commission rules</h2>
            <p className="mt-1 text-sm text-slatey">Generic commission configuration — tiers, schedules, exclusions and qualifying events. Never hard-coded.</p>
            {(form.commission_rules || []).map((rule, index) => (
              <CommissionRuleEditor key={index} rule={rule} index={index}
                onChange={(i, updated) => setForm((c) => { const rules = [...c.commission_rules]; rules[i] = updated; return { ...c, commission_rules: rules }; })}
                onRemove={(i) => setForm((c) => ({ ...c, commission_rules: c.commission_rules.filter((_, ri) => ri !== i) }))} />
            ))}
            <button onClick={() => setForm((c) => ({ ...c, commission_rules: [...(c.commission_rules || []), { label: "", commission_type: "percentage", percentage: null, amount: null, currency: "GBP", qualifying_event: "learner_started", thresholds: "", tiers: [], payment_schedule: [], exclusions: [] }] }))} data-testid="commission-add" className="mt-3 rounded-full border border-slate-300 px-5 py-2.5 text-sm font-bold text-ink">+ Add commission rule</button>
            <StepNav onBack={() => setStep(3)} onNext={() => { save(false); setStep(5); }} nextLabel="Save & review" />
          </div>
        )}

        {step === 5 && detail && (
          <div data-testid="partner-step-review">
            <h2 className="font-display text-xl font-bold text-ink">Review & publish</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl bg-surface p-4"><p className="text-xs font-bold uppercase text-slatey">Partner</p><p className="mt-1 font-bold text-ink">{form.name} · {form.partner_type.replaceAll("_", " ")}</p><p className="text-xs text-slatey">{form.markets}</p></div>
              <div className="rounded-2xl bg-surface p-4"><p className="text-xs font-bold uppercase text-slatey">Configuration</p><p className="mt-1 font-bold capitalize text-ink">Version {config.version} · {configStatus}</p><p className="text-xs text-slatey">Updated by {config.updated_by}</p></div>
              <div className="rounded-2xl bg-surface p-4"><p className="text-xs font-bold uppercase text-slatey">Coverage</p><p className="mt-1 font-bold text-ink">{detail.providers.length} providers · {detail.programmes.length} programmes · {detail.opportunities.length} opportunities</p><p className="text-xs text-slatey">{(form.document_requirements || []).length} document requirements · {(form.sop_sections || []).length} SOP sections · {(form.commission_rules || []).length} commission rules</p></div>
              <div className="rounded-2xl bg-surface p-4"><p className="text-xs font-bold uppercase text-slatey">Submission</p><p className="mt-1 font-bold capitalize text-ink">{form.submission_method.type}</p><p className="text-xs text-slatey">{form.submission_method.recipient || "No recipient configured"}</p></div>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              {configStatus === "draft" && <button onClick={() => transition("review")} data-testid="transition-review" className="rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white">Submit for review</button>}
              {configStatus === "review" && <button onClick={() => transition("approved")} data-testid="transition-approve" className="rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white">Approve</button>}
              {configStatus === "approved" && <button onClick={() => transition("published")} data-testid="transition-publish" className="rounded-full bg-emerald-600 px-5 py-3 text-sm font-bold text-white">Publish configuration</button>}
              {configStatus === "published" && <p className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Published {config.published_at?.slice(0, 10)} by {config.updated_by}. Further edits create a new draft version.</p>}
            </div>

            <h3 className="mt-8 font-display text-lg font-bold text-ink">Change history</h3>
            <ul className="mt-3 space-y-2">
              {[...(config.history || [])].reverse().map((entry, index) => (
                <li key={index} className="rounded-2xl bg-surface px-4 py-2.5 text-xs text-slatey" data-testid={`history-${index}`}>
                  <strong className="text-ink">{entry.action.replaceAll("_", " ")}</strong> · {entry.by} · {entry.at?.slice(0, 16).replace("T", " ")}{entry.note ? ` · ${entry.note}` : ""}
                </li>
              ))}
            </ul>
            <StepNav onBack={() => setStep(4)} onNext={null} />
          </div>
        )}
      </section>

      {id && detail && (
        <section className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="partner-forms-section">
          <h2 className="font-display text-xl font-bold text-ink">Partner forms</h2>
          <p className="mt-1 text-sm leading-6 text-slatey">Upload a partner application form as text. AI will identify the fields and propose mappings to the OpenDegree data model — you review and approve before anything is published.</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-[220px_1fr_auto]">
            <input value={formUpload.name} onChange={(e) => setFormUpload((c) => ({ ...c, name: e.target.value }))} data-testid="form-name" className={input} placeholder="Form name" />
            <textarea value={formUpload.content_text} onChange={(e) => setFormUpload((c) => ({ ...c, content_text: e.target.value }))} data-testid="form-content" rows={2} className={input} placeholder="Paste the partner form fields/questions here…" />
            <button onClick={uploadForm} disabled={formUpload.name.trim().length < 2 || formUpload.content_text.trim().length < 10} data-testid="form-upload" className="rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white disabled:opacity-50"><Upload className="mr-1 inline h-4 w-4" /> Upload</button>
          </div>

          <div className="mt-5 space-y-3">
            {(detail.forms || []).map((formDoc) => (
              <div key={formDoc.id} className="rounded-2xl border border-slate-200 p-4" data-testid={`partner-form-${formDoc.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="font-bold text-ink">{formDoc.name} <span className="ml-2 rounded-full bg-surface px-3 py-1 text-xs font-bold capitalize text-slatey">{formDoc.status.replaceAll("_", " ")}</span></p>
                  <div className="flex gap-2">
                    <button onClick={() => setActiveForm(activeForm === formDoc.id ? null : formDoc.id)} data-testid={`form-expand-${formDoc.id}`} className="rounded-full border border-slate-300 px-4 py-2 text-xs font-bold text-ink">{activeForm === formDoc.id ? "Hide fields" : `Fields (${formDoc.fields?.length || 0})`}</button>
                    {formDoc.status !== "published" && <button onClick={() => analyseForm(formDoc.id)} disabled={analysing} data-testid={`form-analyse-${formDoc.id}`} className="inline-flex items-center gap-1 rounded-full bg-cobalt px-4 py-2 text-xs font-bold text-white disabled:opacity-50">{analysing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} AI analyse</button>}
                    {formDoc.status === "reviewed" && <button onClick={() => publishForm(formDoc.id)} data-testid={`form-publish-${formDoc.id}`} className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-bold text-white">Approve & publish</button>}
                  </div>
                </div>
                {activeForm === formDoc.id && (
                  <FormFieldsEditor key={`${formDoc.id}-${formDoc.fields?.length}`} fields={formDoc.fields || []} readOnly={formDoc.status === "published"}
                    onSave={(fields) => setFieldMapping(formDoc.id, fields)} />
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

const StepNav = ({ onBack, onNext, nextLabel = "Continue" }) => (
  <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-5">
    <button onClick={onBack} data-testid="partner-step-back" className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-bold text-ink">Back</button>
    {onNext && <button onClick={onNext} data-testid="partner-step-next" className="rounded-full bg-cobalt px-5 py-2.5 text-sm font-bold text-white">{nextLabel}</button>}
  </div>
);

const FormFieldsEditor = ({ fields: initialFields, readOnly, onSave }) => {
  const [fields, setFields] = useState(initialFields);
  const update = (index, key, value) => setFields((current) => current.map((field, i) => (i === index ? { ...field, [key]: value } : field)));
  return (
    <div className="mt-4 overflow-x-auto" data-testid="form-fields-editor">
      <table className="w-full min-w-[640px] text-left text-sm">
        <thead><tr className="text-xs font-bold uppercase text-slatey"><th className="pb-2">Partner field</th><th className="pb-2">Type</th><th className="pb-2">Required</th><th className="pb-2">Maps to (OpenDegree)</th><th className="pb-2">Confidence</th></tr></thead>
        <tbody>
          {fields.map((field, index) => (
            <tr key={index} className="border-t border-slate-100" data-testid={`form-field-${index}`}>
              <td className="py-2 pr-3 font-semibold text-ink">{field.partner_field}</td>
              <td className="py-2 pr-3 text-slatey">{field.field_type}</td>
              <td className="py-2 pr-3 text-slatey">{field.required ? "Yes" : "No"}</td>
              <td className="py-2 pr-3">
                <select value={field.mapped_to} disabled={readOnly} onChange={(e) => update(index, "mapped_to", e.target.value)} data-testid={`form-field-mapping-${index}`}
                  className="w-full rounded-xl border border-slate-200 px-2 py-1.5 text-xs outline-none focus:border-cobalt disabled:bg-slate-50">
                  {MAPPING_TARGETS.map((target) => <option key={target} value={target}>{target}</option>)}
                </select>
              </td>
              <td className="py-2 text-xs capitalize text-slatey">{field.confidence}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!readOnly && fields.length > 0 && (
        <button onClick={() => onSave(fields)} data-testid="form-mappings-save" className="mt-4 rounded-full bg-cobalt px-5 py-2.5 text-sm font-bold text-white">Save reviewed mappings</button>
      )}
      {fields.length === 0 && <p className="mt-3 text-sm text-slatey">No fields identified yet — run AI analyse.</p>}
    </div>
  );
};
